import React, {useState, useRef, useEffect} from 'react'
import { Box, Flex, Text, Button, IconButton, Link} from '@salesforce/retail-react-app/app/components/shared/ui';
import {FormattedMessage, useIntl} from 'react-intl';
import {formatDate} from '../../../utils/review-utils';
import {useBreakpointValue} from '@chakra-ui/react'

/* Icons */
import {CheckCircleReverted, ThumbsUp, ThumbsUpDark, VerifiedBuyerIcon} from '../../icons/index';

/* Components */
import StarRating from './star-rating';
import PhotoSlider from './photo-slider';
import PropTypes from 'prop-types';
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'

/* API HOOKS */
import {reviewFeedback, reviewTranslation} from '../../../integrations/bazarvoice/hooks/use-bazarvoice'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getLanguageCode} from '../../../utils/optimizely-preview'
import {
    normalizeLocaleValue,
    getLocaleFromPathname,
    inferLanguageFromText
} from '../utils/review-language-utils'
import {useCustomToast} from '../../../contexts/custom-toast-context'

const ReviewCard = ({ review, setSelectedReview, toastContainerId = 'app-main', configs = {}}) => {
    const intl = useIntl()
    const {showCustomToast, removeToast} = useCustomToast()
    const {locale} = useMultiSite()
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [helpfulnessGiven, setHelpfulnessGiven] = useState(false);
    const [inappropriateGiven, setInappropriateGiven] = useState(false);
    const [positiveFeedbackCount, setPositiveFeedbackCount] = useState(review.TotalPositiveFeedbackCount);
    const [translatedTitle, setTranslatedTitle] = useState(null);
    const [translatedText, setTranslatedText] = useState(null);
    const [isTranslating, setIsTranslating] = useState(false);
    const textRef = useRef(null);
    const reportTriggerKeyRef = useRef(null);
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const defaultConfigs = {
        styles: {
            container: {
                py: 3,
                borderBottom: '1px solid',
                borderBottomColor: 'webTheme.strokeTertiary'
            }
        },
        showDimensionStakedOnMobile: false,
        showMobileVersionToDesktop: false,
        showRecommendation: false,
        showImagesOnTopOfReviewText: false,
        showImages: true,
        showFooterActions: true,
        enableReadMore: false
    };

    const mergedConfigs = {
        styles: {
            container: { ...defaultConfigs.styles.container, ...configs?.styles?.container }
        },
        showDimensionStakedOnMobile : configs?.showDimensionStakedOnMobile ?? defaultConfigs.showDimensionStakedOnMobile,
        showMobileVersionToDesktop: configs?.showMobileVersionToDesktop ?? defaultConfigs.showMobileVersionToDesktop,
        showRecommendation: configs?.showRecommendation ?? defaultConfigs.showRecommendation,
        showImagesOnTopOfReviewText: configs?.showImagesOnTopOfReviewText ?? defaultConfigs.showImagesOnTopOfReviewText,
        showFooterActions: configs?.showFooterActions ?? defaultConfigs.showFooterActions,
        enableReadMore: configs?.enableReadMore ?? defaultConfigs.enableReadMore,
        showImages: configs?.showImages ?? defaultConfigs.showImages
    };

    const {submitFeedback} = reviewFeedback();
    const {translateReview} = reviewTranslation();
    const hasVerifiedPurchase = Boolean(review?.Badges?.isReviewFromVerifiedPurchaseYes)
    const hasReceivedPoints = Boolean(review?.Badges?.isReviewIncentivizedYes)

    const urlLocale = getLocaleFromPathname(
        typeof window !== 'undefined' ? window.location?.pathname || '' : ''
    )
    const currentLocaleSource = normalizeLocaleValue(urlLocale || locale?.id || intl?.locale)
    const reviewLocaleSource = normalizeLocaleValue(review?.ContentLocale || review?.Locale)
    const currentLanguageCode = currentLocaleSource ? getLanguageCode(currentLocaleSource)?.toLowerCase() : ''
    const reviewMetadataLanguageCode = reviewLocaleSource
        ? getLanguageCode(reviewLocaleSource)?.toLowerCase()
        : ''
    const reviewTextLanguageCode = inferLanguageFromText(review?.Title, review?.ReviewText)
    let reviewLanguageCode = reviewMetadataLanguageCode

    // If metadata is missing, use text inference.
    if (!reviewLanguageCode && reviewTextLanguageCode) {
        reviewLanguageCode = reviewTextLanguageCode
    }

    // Bazaarvoice can return en locale metadata for clearly French reviews.
    if (reviewTextLanguageCode === 'fr' && reviewMetadataLanguageCode === 'en') {
        reviewLanguageCode = reviewTextLanguageCode
    }

    // If text strongly matches current language, trust it over conflicting metadata.
    if (
        reviewTextLanguageCode &&
        currentLanguageCode &&
        reviewTextLanguageCode === currentLanguageCode
    ) {
        reviewLanguageCode = reviewTextLanguageCode
    }
    const hasReviewTextContent = Boolean(review?.Title || review?.ReviewText)
    const showTranslateButton =
        hasReviewTextContent &&
        Boolean(currentLanguageCode) &&
        Boolean(reviewLanguageCode) &&
        currentLanguageCode !== reviewLanguageCode
    const displayReviewText = `${hasReceivedPoints ? `${intl.formatMessage({
        defaultMessage: '[This reviewer received Loyalty points in exchange for an honest review]',
        id: 'pdp_reviews.description.received_points_inline'
    })} ` : ''}${translatedText ?? review?.ReviewText ?? ''}`

    /* Hooks */
    useEffect(() => {
        if (textRef.current) {
            setIsOverflowing(textRef.current.scrollHeight > textRef.current.clientHeight);
        }

        // Retrieve feedback data from localStorage
        if (localStorage.getItem('review-feedback')) {
            const feedbackData = JSON.parse(localStorage.getItem('review-feedback')) || {};
            const reviewFeedback = feedbackData[review.Id];

            if (reviewFeedback) {
                setHelpfulnessGiven(reviewFeedback.helpfulnessGiven || false);
                setInappropriateGiven(reviewFeedback.inappropriateGiven || false);
                setPositiveFeedbackCount(reviewFeedback.positiveCount || review.TotalPositiveFeedbackCount);
            } else {
                setHelpfulnessGiven(false);
                setInappropriateGiven(false);
                setPositiveFeedbackCount(review?.TotalPositiveFeedbackCount);
            }
        }
    }, [review?.ReviewText]);

    /* Event handlers */
    const focusTriggerElement = (triggerElement) => {
        if (!triggerElement?.focus) return

        window.requestAnimationFrame(() => {
            try {
                triggerElement.focus({preventScroll: true})
            } catch (error) {
                triggerElement.focus()
            }
        })
    }

    const revertInappropriateFeedback = async (Id, triggerElement = null, shouldRestoreFocus = false) => {
        const revertResult = await submitFeedback({Id, feedback: 'inappropriate'});

        if (revertResult.hasError) {
            return false
        }

        const updatedFeedbackData = localStorage.getItem('review-feedback')
            ? JSON.parse(localStorage.getItem('review-feedback'))
            : {};
        const updatedReviewFeedback = updatedFeedbackData[Id] || {
            positiveCount: review.TotalPositiveFeedbackCount,
            negativeCount: review.TotalInappropriateFeedbackCount
        };

        updatedReviewFeedback.inappropriateGiven = false;
        updatedFeedbackData[Id] = updatedReviewFeedback;
        localStorage.setItem('review-feedback', JSON.stringify(updatedFeedbackData));
        setInappropriateGiven(false);

        if (shouldRestoreFocus) {
            focusTriggerElement(triggerElement)
        }

        return true
    }

    const handleFeedback = async (Id, type, triggerElement = null, triggerKey = null) => {
        let feedbackData = {};

        if (localStorage.getItem('review-feedback')) {
            feedbackData = JSON.parse(localStorage.getItem('review-feedback'));
        }

        const reviewFeedback = feedbackData[Id] || { positiveCount: review.TotalPositiveFeedbackCount, negativeCount: review.TotalInappropriateFeedbackCount };

        if (type === 'helpfulness' && !helpfulnessGiven) {
            const result = await submitFeedback({ Id, feedback: type });

            if (result.hasError) {
                return;
            }

            const newCount = reviewFeedback.positiveCount + 1;
            setPositiveFeedbackCount(newCount);
            reviewFeedback.positiveCount = newCount;
            reviewFeedback.helpfulnessGiven = true;
            setHelpfulnessGiven(true);
        } else if (type === 'inappropriate' && !inappropriateGiven) {
            const result = await submitFeedback({ Id, feedback: type });

            if (result.hasError) {
                return;
            }

            reviewFeedback.inappropriateGiven = true;
            setInappropriateGiven(true);
            let reportToastId;
            reportToastId = showCustomToast({
                message: intl.formatMessage({
                    defaultMessage: 'Report submitted.',
                    id: 'pdp_reviews.footer.report_submitted'
                }),
                actionLabel: intl.formatMessage({
                    id: 'cart_minicart.button.undo',
                    defaultMessage: 'Undo'
                }),
                onActionClick: async (event, metadata = {}) => {
                    const reverted = await revertInappropriateFeedback(
                        Id,
                        triggerElement,
                        metadata.triggerKey === 'Enter'
                    )

                    if (reverted && reportToastId) {
                        removeToast(reportToastId);
                    }
                },
                onManualClose: () => {
                    void revertInappropriateFeedback(Id)
                },
                duration: 20000,
                styles: {
                    position: 'fixed',
                    top: 'auto',
                    bottom: isDesktop ? '120px' : '100px',
                    width: '100%',
                    maxWidth: isDesktop ? '375px' : 'auto',
                    right: 0,
                    left: isDesktop ? '24px' : 0
                },
                containerId: toastContainerId,
                returnFocusElement: triggerElement,
                focusActionOnMount: triggerKey === 'Enter' || triggerKey === ' ',
                pauseOnHover: true
            });
        }

        feedbackData[Id] = reviewFeedback;
        localStorage.setItem('review-feedback', JSON.stringify(feedbackData));
    };

    const handleReportKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            reportTriggerKeyRef.current = event.key
        }
    }

    const handleReportClick = (event) => {
        if (event) event.preventDefault()

        const triggerKey = reportTriggerKeyRef.current
        reportTriggerKeyRef.current = null
        handleFeedback(review?.Id, 'inappropriate', event?.currentTarget, triggerKey)
    }

    const handleTranslate = async () => {
        if (translatedText) {
            setTranslatedTitle(null);
            setTranslatedText(null);
            return;
        }
        setIsTranslating(true);
        const targetLocale = locale?.id || 'en_US';
        const combinedText = `${review?.Title}|||${review?.ReviewText}`;
        const result = await translateReview({reviewText: combinedText, targetLocale});
        if (result?.translatedText) {
            const parts = result.translatedText.split('|||');
            setTranslatedTitle(parts[0]?.trim() || '');
            setTranslatedText(parts[1]?.trim() || result.translatedText);
        }
        setIsTranslating(false);
    };

    const toggleReadMore = () => {
        setIsExpanded(!isExpanded);
    };

    /* Review Template */
    return (
        <Box {...mergedConfigs.styles.container} className="review-card">
            <HideOnDesktop>
                <Flex justifyContent="space-between" flex={1}
                    mb={{base: 2, lg: mergedConfigs.showMobileVersionToDesktop ? 3 : 2}}
                    lineHeight={{base: '18px'}}>
                    {/* Stars and Date - Mobile */}
                    <Box lineHeight={{base: '16px'}}>
                        <StarRating
                            key={review?.Id}
                            initialRating={Number(review?.Rating)}
                            lock={true} 
                            styles={{
                                starWidth: '16',
                                starHeight: '16'
                            }}
                        />
                    </Box>
                    {/* Submission time */}
                    <Box display={{base: mergedConfigs.showMobileVersionToDesktop ? 'none' : 'block'}}>
                        <Text 
                            textStyle={'bodyXsRegular'}
                            color={{base: 'webBasic.950', lg: mergedConfigs.showMobileVersionToDesktop ? 'webBasic.950' : 'webTypo.textPrimary'}}>
                            {formatDate(review?.SubmissionTime, locale?.id)}
                        </Text>
                    </Box>
                </Flex>
            </HideOnDesktop>

            <Flex flexDirection={{base: 'column', lg: mergedConfigs.showMobileVersionToDesktop ? 'column' : 'row'}}
                gap={{lg: mergedConfigs.showMobileVersionToDesktop ? '' : '80px'}}>
                {/* Column 1 - Nickname and Verified buyer */}
                {/* Stars */}
                <Box mb={{base: 8, lg: 0}} mt={{lg: 0}}
                    mt={{lg: 0}}
                    flexBasis={{lg: mergedConfigs.showMobileVersionToDesktop ? '' : '125px'}}
                    flexShrink={{lg: mergedConfigs.showMobileVersionToDesktop ? '' : '0'}}>
                    <HideOnMobile>
                        <Box lineHeight={{base: '16px'}}>
                            <StarRating
                                key={review?.Id}
                                initialRating={Number(review?.Rating)}
                                lock={true} styles={{starWidth: '16',
                                starHeight: '16'}} />
                        </Box>
                    </HideOnMobile>

                    {/* User name */}
                    <Text as="h4" textStyle="webH4" pt={2}>
                        {review?.UserNickname}
                    </Text>

                    {/* Review badges */}
                    {hasVerifiedPurchase && (
                        <Flex alignItems={'center'}
                            mt={{base: 2, lg: mergedConfigs.showMobileVersionToDesktop ? 4 : 2}}>
                            <CheckCircleReverted boxSize={5} />
                            <Text
                                textStyle='bodyXsRegular'
                                ml={{base: 1}}>
                                <FormattedMessage
                                    defaultMessage="Verified Buyer"
                                    id="pdp_reviews.description.verified"
                                />
                            </Text>
                        </Flex>
                    )}

                    {/* Dimensions */}
                    <Flex display={{base: mergedConfigs.showDimensionStakedOnMobile ? 'block' : 'flex'}}
                        flexDirection={{base: mergedConfigs.showDimensionStakedOnMobile ? 'column' : '', lg: 'column'}}
                        flexWrap={{base: 'wrap'}}
                        mt={{base: 6}}>
                        {/* Height & Weight */}
                        {Object.keys(review?.ContextDataValues).map((key, index) => {
                            const contextData = review?.ContextDataValues[key];

                            const keyLowerCase = key.toLowerCase();
                            if (keyLowerCase == "height" || keyLowerCase == "weight") {
                                const dimensionLabel = keyLowerCase === 'height'
                                    ? intl.formatMessage({
                                        defaultMessage: 'Height',
                                        id: 'pdp_reviews.dimension.height'
                                    })
                                    : intl.formatMessage({
                                        defaultMessage: 'Approx. Weight',
                                        id: 'pdp_reviews.dimension.weight'
                                    });

                                return (<Text key={'dim-' + index} as={'span'}
                                    textStyle='bodyXsRegular'
                                    mb={2}>
                                    {dimensionLabel} : {contextData?.ValueLabel} {((!mergedConfigs.showDimensionStakedOnMobile && !isDesktop) || (mergedConfigs.showDimensionStakedOnMobile)) && (<Text as={'span'} mx={{base: 1}}>/</Text>)}
                                </Text>)
                            }
                        })}

                        {/* Additional fields - Size */}
                        {Object.keys(review?.AdditionalFields).map((key, index) => {
                            const additionalFields = review?.AdditionalFields[key];

                            return (<Text key={'add-' + index} as={'span'}
                                textStyle='bodyXsRegular'
                                mb={2}>
                                {additionalFields?.Label} : {additionalFields?.Value} {((!mergedConfigs.showDimensionStakedOnMobile && !isDesktop) || (mergedConfigs.showDimensionStakedOnMobile)) && (<Text as={'span'} mx={{base: 1}}>/</Text>)}
                            </Text>)
                        })}

                        {/* Fit */}
                        {review?.SecondaryRatings?.HowDoesItFit?.ValueLabel && (
                            <Text as={'span'}
                                textStyle='bodyXsRegular'>
                                <FormattedMessage
                                    defaultMessage="Actual Fit"
                                    id="pdp_reviews.description.fit"
                                /> : {review?.SecondaryRatings?.HowDoesItFit?.ValueLabel}
                            </Text>
                        )}
                    </Flex>
                </Box>

                {/* Column 2 - Title, Photos */}
                <Flex flexBasis={{lg: mergedConfigs.showMobileVersionToDesktop ? '' : '100%'}}
                    flexDirection={{base: 'column'}}
                    justifyContent={{base: 'space-between'}}
                    minWidth={{lg: 0}}>
                    <Box>
                        {/* Date - for modal */}
                        <Text 
                            display={{base: mergedConfigs.showMobileVersionToDesktop ? 'flex' : 'none'}}
                            textStyle={'bodyXsRegular'}
                            textAlign={{lg: 'right'}}
                            color={{base: 'webTypo.textPrimary', lg: mergedConfigs.showMobileVersionToDesktop ? 'webBasic.950' : 'webTypo.textPrimary'}}
                            mt={{base: mergedConfigs.showMobileVersionToDesktop ? 6 : 0}}>
                            {formatDate(review?.SubmissionTime, locale?.id)}
                        </Text>

                        {/* Review Title */}
                        <Text textStyle='bodyXsSMedium'
                            mb={{base: 4}}>
                            {translatedTitle ?? review?.Title}
                        </Text>
                        {mergedConfigs.showImages && mergedConfigs.showImagesOnTopOfReviewText && review?.Photos.length > 0 && (
                            <Box
                                mb={{base: 4, lg: mergedConfigs.showMobileVersionToDesktop ? 4 : 6}}
                                py="2px"
                            >
                                <PhotoSlider review={review} setSelectedReview={setSelectedReview} />
                            </Box>
                        )}

                        <Text ref={textRef}
                            textStyle='bodySmLink'
                            textDecor={'none'}
                            mb={{base: 4}}
                            sx={mergedConfigs.enableReadMore && {
                                display: '-webkit-box',
                                WebkitLineClamp: isExpanded ? 'none' : 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>
                            {displayReviewText}
                        </Text>
                        {mergedConfigs.enableReadMore && isOverflowing  && !isExpanded &&
                            <Button variant={'link'}
                                fontSize={{base: 'xs'}}
                                letterSpacing={{base: '1%'}}
                                lineHeight={{base: '15px'}}
                                onClick={toggleReadMore}
                                mt={{base: 4}}>
                                <FormattedMessage
                                    defaultMessage="Read More"
                                    id="pdp_reviews.text.read.more.link"
                                />
                            </Button>
                        }

                        {/* Translate - Google translate content */}
                        {showTranslateButton && (
                            <Button
                                textStyle='bodySmLink'
                                textTransform={'underline'}
                                bgColor={'transparent'}
                                color={'webTypo.textPrimary'}
                                _hover={{
                                    bgColor: 'transparent'
                                }}
                                fontWeight={'normal'}
                                padding={0}
                                height={5}
                                isLoading={isTranslating}
                                onClick={handleTranslate}>
                                {translatedText ? (
                                    <FormattedMessage
                                        defaultMessage="Show original"
                                        id="pdp_reviews.translate.show_original"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Translate with Google"
                                        id="pdp_reviews.translate.button"
                                    />
                                )}
                            </Button>
                        )}

                        {/* Images */}
                        {mergedConfigs.showImages && !mergedConfigs.showImagesOnTopOfReviewText && review?.Photos.length > 0 && (
                            <Box
                                mt={{base: 4, lg: mergedConfigs.showMobileVersionToDesktop ? 4 : 4}}
                                py="2px"
                            >
                                <PhotoSlider review={review} setSelectedReview={setSelectedReview}/>
                            </Box>
                        )}
                    </Box>

                    {/* Like section */}
                    <Flex justifyContent={{base: 'space-between'}}
                        alignItems={{base: 'center'}}
                        mt={{base: 8, lg: mergedConfigs.showMobileVersionToDesktop ? 4 : 4}}
                        mb={{base: 2}}
                        display={mergedConfigs.showFooterActions ? 'flex' : 'none'}>
                        <Flex alignItems={{base: 'flex-end'}}
                            lineHeight="24px">
                            <Text textStyle='bodyXsRegular'
                                mr={{base: 6}}>
                                <FormattedMessage
                                    defaultMessage="Was this helpful?"
                                    id="pdp_reviews.footer.helpful"
                                />
                            </Text>
                            <Flex alignItems={{base: 'flex-end'}}>
                                {helpfulnessGiven &&
                                    <IconButton
                                        as={Link}
                                        href='#'
                                        variant="unstyled"
                                        h={'auto'}
                                        minW={'auto'}
                                        cursor={'unset'}
                                        onClick={(e) => {
                                            if (e) e.preventDefault()
                                            handleFeedback(review?.Id, 'helpfulness')
                                        }}
                                        icon={<ThumbsUpDark width={{base: '20px'}} height={{base: '20px'}} />}
                                        aria-label="Like report"
                                    />
                                }
                                {!helpfulnessGiven &&
                                    <IconButton
                                        as={Link}
                                        href='#'
                                        variant="unstyled"
                                        h={'auto'}
                                        minW={'auto'}
                                        onClick={(e) => {
                                            if(e) e.preventDefault()
                                            handleFeedback(review?.Id, 'helpfulness')
                                        }}
                                        icon={<ThumbsUp width={{base: '20px'}} height={{base: '20px'}} />}
                                        aria-label="Unlike report"
                                    />
                                }
                                <Text textStyle='bodyXsRegular'
                                    ml={{base: 1}}>
                                    ({positiveFeedbackCount})
                                </Text>
                            </Flex>
                        </Flex>

                        {/* Report review */}
                        <Box lineHeight="18px" display={{base: 'block', lg: mergedConfigs.showMobileVersionToDesktop ? 'block' : 'none'}}>
                            <Button
                                as={Link}
                                href='#'
                                variant="link"
                                aria-label="Report the review"
                                fontSize={{base: '12px'}}
                                lineHeight={5}
                                // textStyle='bodyXsLink'
                                textDecoration={inappropriateGiven ? 'none' : 'underline'}
                                _hover={{
                                    textDecoration: inappropriateGiven ? 'none' : 'underline'
                                }}
                                cursor={inappropriateGiven ? 'default' : 'pointer'}
                                onKeyDown={handleReportKeyDown}
                                onClick={handleReportClick}>
                                {inappropriateGiven ? (
                                    <FormattedMessage
                                        defaultMessage="Reported"
                                        id="pdp_reviews.footer.reported"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Report"
                                        id="pdp_reviews.footer.report"
                                    />
                                )}
                            </Button>
                        </Box>
                    </Flex>
                </Flex>

                {/* Column 3 - Date & Report */}
                <Flex flexWrap={{lg: 'wrap'}} 
                    flexBasis={{lg: '175px'}} 
                    justifyContent={{lg: 'right'}} 
                    alignSelf="stretch"
                    display={{base: 'none', lg: mergedConfigs.showMobileVersionToDesktop ? 'none' : 'flex'}}>
                    {/* Submission time */}
                    <Flex>
                        <Text 
                            textStyle={'bodyXsRegular'}
                            textAlign={{lg: 'right'}}
                            color={{base: 'webBasic.950', lg: mergedConfigs.showMobileVersionToDesktop ? 'webBasic.950' : 'webTypo.textPrimary'}}>
                            {formatDate(review?.SubmissionTime, locale?.id)}
                        </Text>
                    </Flex>

                    {/* Report */}
                    <Flex alignItems={{lg: 'flex-end'}} lineHeight="18px">
                        <Button
                            as={Link}
                            href='#'
                            variant="link"
                            aria-label="Report the review"
                            // textStyle='bodyXsLink'
                            fontSize={{base: '12px'}}
                            lineHeight={5}
                            textDecoration={inappropriateGiven ? 'none' : 'underline'}
                            _hover={{
                                textDecoration: inappropriateGiven ? 'none' : 'underline'
                            }}
                            cursor={inappropriateGiven ? 'default' : 'pointer'}
                            onKeyDown={handleReportKeyDown}
                            onClick={handleReportClick}>
                            {inappropriateGiven ? (
                                <FormattedMessage
                                    defaultMessage="Reported"
                                    id="pdp_reviews.footer.reported"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Report"
                                    id="pdp_reviews.footer.report"
                                />
                            )}
                        </Button>
                    </Flex>
                </Flex>
            </Flex>
        </Box>
    );
};

ReviewCard.propTypes = {
    review: PropTypes.object,
    setSelectedReview: PropTypes.func,
    configs: PropTypes.object,
    containerId: PropTypes.string
};

export default ReviewCard;
